'use client';
import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, X, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

interface GeolocationModalProps {
  onClose: (coords?: { lat: number; lng: number }) => void;
}

export default function GeolocationModal({ onClose }: GeolocationModalProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'manual'>('idle');
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [manualError, setManualError] = useState('');

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setStatus('manual');
      return;
    }
    setStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const c = {
          lat: parseFloat(position.coords.latitude.toFixed(6)),
          lng: parseFloat(position.coords.longitude.toFixed(6)),
        };
        setCoords(c);
        setStatus('success');
      },
      () => {
        setStatus('error');
      },
      { timeout: 10000 }
    );
  };

  useEffect(() => {
    // Auto-request on mount
    requestLocation();
  }, []);

  const handleManualSubmit = () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      setManualError('Latitude inválida. Use um valor entre -90 e 90.');
      return;
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      setManualError('Longitude inválida. Use um valor entre -180 e 180.');
      return;
    }
    setManualError('');
    onClose({ lat, lng });
  };

  const handleConfirm = () => {
    if (coords) onClose(coords);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #2CC4BE22, #104CAE22)' }}
            >
              <MapPin size={20} className="text-battery-turquoise" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Localização</h2>
              <p className="text-xs text-muted-foreground">Necessária para simulações precisas</p>
            </div>
          </div>
          <button
            onClick={() => onClose()}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted/50 transition-all duration-150 text-muted-foreground"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        {status === 'loading' && (
          <div className="flex flex-col items-center py-8 gap-4">
            <Loader2 size={40} className="text-battery-turquoise animate-spin" />
            <p className="text-sm text-muted-foreground text-center">
              Solicitando permissão de localização...
            </p>
          </div>
        )}

        {status === 'success' && coords && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 border border-green-200">
              <CheckCircle2 size={20} className="text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-green-800">
                  Localização obtida com sucesso!
                </p>
                <p className="text-xs text-green-700 mt-0.5">
                  Lat: <span className="font-mono font-bold">{coords.lat}</span> &nbsp;|&nbsp; Lng:{' '}
                  <span className="font-mono font-bold">{coords.lng}</span>
                </p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Sua localização será usada para calcular tarifas regionais e irradiação solar nas
              simulações de BESS.
            </p>
            <button onClick={handleConfirm} className="btn-primary w-full py-3 mt-2">
              <CheckCircle2 size={16} />
              Confirmar e continuar
            </button>
          </div>
        )}

        {(status === 'error' || status === 'manual') && (
          <div className="flex flex-col gap-4">
            {status === 'error' && (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-yellow-50 border border-yellow-200">
                <AlertCircle size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  Não foi possível obter sua localização automaticamente. Por favor, insira
                  manualmente.
                </p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              Informe as coordenadas geográficas da instalação para simulações precisas.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label-text">Latitude</label>
                <div className="relative">
                  <Navigation
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder="-23.550520"
                    value={manualLat}
                    onChange={(e) => setManualLat(e.target.value)}
                    className="input-field pl-8 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="label-text">Longitude</label>
                <div className="relative">
                  <Navigation
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground rotate-90"
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder="-46.633308"
                    value={manualLng}
                    onChange={(e) => setManualLng(e.target.value)}
                    className="input-field pl-8 text-sm"
                  />
                </div>
              </div>
            </div>
            {manualError && <p className="text-xs text-red-500">{manualError}</p>}
            <div className="flex gap-3 mt-2">
              <button onClick={() => onClose()} className="flex-1 btn-secondary py-2.5 text-sm">
                Pular por agora
              </button>
              <button onClick={handleManualSubmit} className="flex-1 btn-primary py-2.5 text-sm">
                <MapPin size={15} />
                Confirmar
              </button>
            </div>
          </div>
        )}

        {status === 'idle' && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Para simulações mais precisas de BESS, precisamos da sua localização geográfica para
              calcular tarifas regionais e irradiação solar.
            </p>
            <div className="flex flex-col gap-3 mt-2">
              <button onClick={requestLocation} className="btn-primary w-full py-3">
                <Navigation size={16} />
                Usar minha localização atual
              </button>
              <button onClick={() => setStatus('manual')} className="btn-secondary w-full py-3">
                <MapPin size={16} />
                Inserir manualmente
              </button>
              <button
                onClick={() => onClose()}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                Pular por agora
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
